import { RiskCompanyDTO, RiskEpiCatalogDTO, RiskLinkDTO, RiskSheetRowDTO } from "../../types/RiskAssessmentDTO";
import {
	ASSESSMENT_SIZE, ASSESSMENT_TAB, CA_TAB, EPI_TAB, LINE_HEIGHT, LinkKind, MATRIX, NEW_LINE_HEIGHT, POSITION, RISK, RiskColumn,
	SECTOR, SUB_TABS, TAB_SIZES
} from "./riskColumns";

export interface EditorLine {
	id: string;
	values: string[];
	original: string[] | null;
	removed: boolean;
	caLineId?: string;
	originalCaLineId?: string;
}

export interface EditorRow {
	key: string;
	sourceLine: number;
	values: string[];
	original: string[];
	removed: boolean;
	added?: boolean;
	tabs: Record<string, EditorLine[]>;
}

export interface SelectionState {
	rows: Set<string>;
	lines: Set<string>;
}

export interface EditingCell {
	rowKey: string;
	lineId?: string;
	columnId: string;
}

export interface LinkIndex {
	byRisk: Map<string, RiskLinkDTO>;
	catalog: Map<number, RiskEpiCatalogDTO>;
}

export interface RiskGridHandlers {
	onToggleRow: (row: EditorRow) => void;
	onToggleLine: (row: EditorRow, lineId: string) => void;
	onStartEdit: (cell: EditingCell) => void;
	onCommitEdit: (cell: EditingCell, value: string) => void;
	onCancelEdit: () => void;
	onToggleRowRemoved: (row: EditorRow) => void;
	onAddLine: (row: EditorRow) => void;
	onRemoveLine: (row: EditorRow, line: EditorLine) => void;
	onRestoreLine: (row: EditorRow, line: EditorLine) => void;
	onRevertRow?: (row: EditorRow) => void;
}

export type Filters = Record<string, string[] | string>;
export type BulkValues = Record<string, string>;
export type CheckState = 'checked' | 'indeterminate' | 'unchecked';

export const EMPTY_OPTION = '__EMPTY__';
export const LINK_TYPES = ['EPI', 'EPC', 'MEASURE'];
export const LINK_SOURCE_COMPANY = 'Base de consistência de vínculo - 554';
export const RECOMMENDED_EPI = 0;
export const UTILIZED_EPI = 1;

const EPI_CA_SEPARATOR = '\t';

const SESSION_PREFIX = Date.now().toString(36);
let lineSequence = 0;
let rowSequence = 0;

export function buildEditorRows(rows: RiskSheetRowDTO[], catalog: RiskEpiCatalogDTO[] = []): EditorRow[] {
	const names = new Map(catalog.map(item => [String(item.code), epiNameKey(item.name)] as [string, string]));
	return rows.map(row => pairCaLinesByName({
		key: row.key,
		sourceLine: row.sourceLine,
		values: [...row.values],
		original: [...row.values],
		removed: false,
		tabs: Object.fromEntries(SUB_TABS.map(tab => [tab, (row.tabs[tab] || []).map((values, index) => ({
			id: `${row.key}~${tab}~${index}`,
			values: [...values],
			original: [...values],
			removed: false
		}))]))
	}, names));
}

export function epiNameKey(name: string): string {
	return normalizeText(name || '').replace(/^[\s-]+/, '').replace(/\s+/g, ' ').trim();
}

function pairCaLinesByName(row: EditorRow, names: Map<string, string>): EditorRow {
	const used = new Set<string>();
	const caLines = row.tabs[CA_TAB] || [];
	return updateLines(row, EPI_TAB, lines => lines.map(line => {
		const key = epiNameKey(line.values[UTILIZED_EPI]);
		const caLine = key ? caLines.find(item => !used.has(item.id) && names.get((item.values[0] || '').trim()) === key) : undefined;
		if (caLine) {
			used.add(caLine.id);
		}
		return { ...line, caLineId: caLine?.id, originalCaLineId: caLine?.id };
	}));
}

export function createLine(rowKey: string, tab: string, values?: string[]): EditorLine {
	lineSequence += 1;
	return {
		id: `${rowKey}~${tab}~n${SESSION_PREFIX}-${lineSequence}`,
		values: values || Array(TAB_SIZES[tab]).fill(''),
		original: null,
		removed: false
	};
}

export function activeLines(row: EditorRow, tab: string): EditorLine[] {
	return (row.tabs[tab] || []).filter(line => !line.removed);
}

export function isLineChanged(line: EditorLine): boolean {
	const original = line.original;
	if (original === null) {
		return !line.removed && line.values.some(value => (value || '').trim() !== '');
	}
	return line.removed || line.values.some((value, index) => value !== original[index]);
}

export function isRowChanged(row: EditorRow): boolean {
	return !!row.added || row.removed
		|| row.values.some((value, index) => value !== row.original[index])
		|| SUB_TABS.some(tab => (row.tabs[tab] || []).some(isLineChanged));
}

function replaceAt(values: string[], index: number, value: string): string[] {
	const next = [...values];
	next[index] = value;
	return next;
}

function updateRow(rows: EditorRow[], rowKey: string, update: (row: EditorRow) => EditorRow): EditorRow[] {
	return rows.map(row => row.key === rowKey ? update(row) : row);
}

function updateLines(row: EditorRow, tab: string, update: (lines: EditorLine[]) => EditorLine[]): EditorRow {
	return { ...row, tabs: { ...row.tabs, [tab]: update(row.tabs[tab] || []) } };
}

export function setRowValue(rows: EditorRow[], rowKey: string, index: number, value: string): EditorRow[] {
	return updateRow(rows, rowKey, row => row.values[index] === value
		? row
		: { ...row, values: replaceAt(row.values, index, value) });
}

export function setLineValue(rows: EditorRow[], rowKey: string, tab: string, lineId: string, index: number,
		value: string): EditorRow[] {
	return updateRow(rows, rowKey, row => updateLines(row, tab, lines => lines.map(line =>
		line.id !== lineId || line.values[index] === value ? line : { ...line, values: replaceAt(line.values, index, value) })));
}

export function appendLine(rows: EditorRow[], rowKey: string, tab: string, line: EditorLine): EditorRow[] {
	return updateRow(rows, rowKey, row => updateLines(row, tab, lines => [...lines, line]));
}

function dropLine(row: EditorRow, tab: string, lineId: string): EditorRow {
	return updateLines(row, tab, lines => lines
		.filter(line => line.id !== lineId || line.original !== null)
		.map(line => line.id === lineId ? { ...line, removed: true } : line));
}

function reviveLine(row: EditorRow, tab: string, lineId: string): EditorRow {
	return updateLines(row, tab, lines => lines.map(line => line.id === lineId ? { ...line, removed: false } : line));
}

function pairedCaLine(row: EditorRow, epiLineId: string): EditorLine | undefined {
	const epiLine = (row.tabs[EPI_TAB] || []).find(line => line.id === epiLineId);
	return epiLine?.caLineId ? (row.tabs[CA_TAB] || []).find(line => line.id === epiLine.caLineId) : undefined;
}

export function removeLine(rows: EditorRow[], rowKey: string, tab: string, lineId: string): EditorRow[] {
	return updateRow(rows, rowKey, row => {
		const caLine = tab === EPI_TAB ? pairedCaLine(row, lineId) : undefined;
		const next = dropLine(row, tab, lineId);
		return caLine ? dropLine(next, CA_TAB, caLine.id) : next;
	});
}

export function restoreLine(rows: EditorRow[], rowKey: string, tab: string, lineId: string): EditorRow[] {
	return updateRow(rows, rowKey, row => {
		const caLine = tab === EPI_TAB ? pairedCaLine(row, lineId) : undefined;
		const next = reviveLine(row, tab, lineId);
		return caLine ? reviveLine(next, CA_TAB, caLine.id) : next;
	});
}

function withCaCode(row: EditorRow, epiLineId: string, code: string): EditorRow {
	const caLine = pairedCaLine(row, epiLineId);
	if (!code) {
		return caLine ? dropLine(row, CA_TAB, caLine.id) : row;
	}
	const existing = activeLines(row, CA_TAB).find(line => line.id !== caLine?.id && (line.values[0] || '').trim() === code);
	if (existing) {
		const withoutOwn = caLine ? dropLine(row, CA_TAB, caLine.id) : row;
		return updateLines(withoutOwn, EPI_TAB, lines => lines.map(line => line.id === epiLineId ? { ...line, caLineId: existing.id } : line));
	}
	if (caLine) {
		return caLine.values[0] === code && !caLine.removed ? row : updateLines(row, CA_TAB, lines => lines.map(line => line.id === caLine.id
			? { ...line, values: replaceAt(line.values, 0, code), removed: false }
			: line));
	}
	const created = createLine(row.key, CA_TAB, [code]);
	const withLine = updateLines(row, CA_TAB, lines => [...lines, created]);
	return updateLines(withLine, EPI_TAB, lines => lines.map(line => line.id === epiLineId ? { ...line, caLineId: created.id } : line));
}

export function caLineWarning(row: EditorRow, line: EditorLine, index: LinkIndex): string | null {
	const code = (line.values[0] || '').trim();
	if (!code || line.removed || activeLines(row, EPI_TAB).some(epi => epi.caLineId === line.id)) {
		return null;
	}
	return index.catalog.has(Number(code))
		? 'Este CA não está ligado a nenhum EPI utilizado deste risco na aba EPI(s).'
		: 'Código não encontrado no catálogo de EPIs e sem EPI utilizado ligado a ele.';
}

export function epiOption(epi: string, code?: string | number | null): string {
	return code === undefined || code === null || code === '' ? epi : `${epi}${EPI_CA_SEPARATOR}${code}`;
}

export function parseEpiOption(option: string): { epi: string; code: string } {
	const [epi, code] = (option || '').split(EPI_CA_SEPARATOR);
	return { epi: epi || '', code: code || '' };
}

export function setUtilizedEpi(rows: EditorRow[], rowKey: string, lineId: string, option: string): EditorRow[] {
	const { epi, code } = parseEpiOption(option);
	return updateRow(rows, rowKey, row => {
		const current = (row.tabs[EPI_TAB] || []).find(line => line.id === lineId);
		if (current && current.values[UTILIZED_EPI] === epi && !code) {
			return row;
		}
		return withCaCode(updateLines(row, EPI_TAB, lines => lines.map(line =>
			line.id !== lineId || line.values[UTILIZED_EPI] === epi ? line : { ...line, values: replaceAt(line.values, UTILIZED_EPI, epi) })),
			lineId, epi ? code : '');
	});
}

export function setRecommendedEpi(rows: EditorRow[], rowKey: string, lineId: string, option: string): EditorRow[] {
	const { epi, code } = parseEpiOption(option);
	const row = rows.find(item => item.key === rowKey);
	const line = row ? (row.tabs[EPI_TAB] || []).find(item => item.id === lineId) : undefined;
	if (line && line.values[RECOMMENDED_EPI] === epi && !code) {
		return rows;
	}
	const withRecommended = updateRow(rows, rowKey, row => updateLines(row, EPI_TAB, lines => lines.map(line =>
		line.id !== lineId || line.values[RECOMMENDED_EPI] === epi ? line : { ...line, values: replaceAt(line.values, RECOMMENDED_EPI, epi) })));
	return setUtilizedEpi(withRecommended, rowKey, lineId, option);
}

export function utilizedEpiValue(row: EditorRow, line: EditorLine): string {
	const caLine = pairedCaLine(row, line.id);
	return epiOption(line.values[UTILIZED_EPI] || '', caLine && !caLine.removed ? caLine.values[0] : '');
}

export function isUtilizedEpi(column: RiskColumn): boolean {
	return column.tab === EPI_TAB && column.index === UTILIZED_EPI;
}

export function isRecommendedEpi(column: RiskColumn): boolean {
	return column.tab === EPI_TAB && column.index === RECOMMENDED_EPI;
}

function withoutAddedRow(rows: EditorRow[], rowKey: string): EditorRow[] | null {
	return rows.some(row => row.key === rowKey && row.added) ? rows.filter(row => row.key !== rowKey) : null;
}

export function toggleRowRemoved(rows: EditorRow[], rowKey: string): EditorRow[] {
	return withoutAddedRow(rows, rowKey) || updateRow(rows, rowKey, row => ({ ...row, removed: !row.removed }));
}

export function createAddedRows(sector: string, positions: string[], risk: string, template?: EditorRow): EditorRow[] {
	return positions.map(position => {
		rowSequence += 1;
		const key = `n${SESSION_PREFIX}-${rowSequence}`;
		const values = template ? [...template.values] : Array(ASSESSMENT_SIZE).fill('');
		values[SECTOR] = sector;
		values[POSITION] = position;
		values[RISK] = risk;
		return {
			key,
			sourceLine: -rowSequence,
			values,
			original: [...values],
			removed: false,
			added: true,
			tabs: template ? copyTemplateLines(key, template) : Object.fromEntries(SUB_TABS.map(tab => [tab, []]))
		};
	});
}

function copyTemplateLines(key: string, template: EditorRow): Record<string, EditorLine[]> {
	const caIds = new Map<string, string>();
	const tabs = Object.fromEntries(SUB_TABS.map(tab => [tab, activeLines(template, tab).map(line => {
		const copy = createLine(key, tab, [...line.values]);
		caIds.set(line.id, copy.id);
		return copy;
	})]));
	tabs[EPI_TAB] = tabs[EPI_TAB].map((copy, index) => {
		const caLineId = activeLines(template, EPI_TAB)[index].caLineId;
		return caLineId && caIds.has(caLineId) ? { ...copy, caLineId: caIds.get(caLineId) } : copy;
	});
	return tabs;
}

function sameText(first: string, second: string): boolean {
	return normalizeText((first || '').trim()) === normalizeText((second || '').trim());
}

export function insertAddedRows(rows: EditorRow[], added: EditorRow[]): EditorRow[] {
	return added.reduce((result, row) => {
		const lastIndex = (match: (item: EditorRow) => boolean) => result.reduce((found, item, index) => match(item) ? index : found, -1);
		const samePosition = lastIndex(item => sameText(item.values[SECTOR], row.values[SECTOR])
			&& sameText(item.values[POSITION], row.values[POSITION]));
		const sameSector = samePosition >= 0 ? samePosition : lastIndex(item => sameText(item.values[SECTOR], row.values[SECTOR]));
		const position = sameSector >= 0 ? sameSector + 1 : result.length;
		return [...result.slice(0, position), row, ...result.slice(position)];
	}, rows);
}

function sortedDistinct(values: string[]): string[] {
	return Array.from(new Set(values.filter(value => !!value)))
		.sort((first, second) => first.localeCompare(second, 'pt-BR', { sensitivity: 'base' }));
}

export function templateChoices(rows: EditorRow[], sector: string | null,
		position: string | null): { sectors: string[]; positions: string[]; risks: string[] } {
	const active = rows.filter(row => !row.removed);
	const inSector = sector ? active.filter(row => row.values[SECTOR] === sector) : [];
	const inPosition = position ? inSector.filter(row => row.values[POSITION] === position) : [];
	return {
		sectors: sortedDistinct(active.map(row => row.values[SECTOR])),
		positions: sortedDistinct(inSector.map(row => row.values[POSITION])),
		risks: sortedDistinct(inPosition.map(row => row.values[RISK]))
	};
}

export function findTemplate(rows: EditorRow[], sector: string, position: string, risk: string): EditorRow | null {
	return rows.find(row => !row.removed && row.values[SECTOR] === sector && row.values[POSITION] === position
		&& row.values[RISK] === risk) || null;
}

export function duplicatePositions(rows: EditorRow[], sector: string, positions: string[], risk: string): string[] {
	return positions.filter(position => rows.some(row => !row.removed && sameText(row.values[SECTOR], sector)
		&& sameText(row.values[POSITION], position) && riskKey(row.values[RISK]) === riskKey(risk)));
}

export function revertRow(rows: EditorRow[], rowKey: string): EditorRow[] {
	return withoutAddedRow(rows, rowKey) || updateRow(rows, rowKey, row => ({
		...row,
		values: [...row.original],
		removed: false,
		tabs: Object.fromEntries(Object.entries(row.tabs).map(([tab, lines]) => [tab, lines.flatMap(line => line.original
			? [{ ...line, values: [...line.original], removed: false, caLineId: line.originalCaLineId }]
			: [])]))
	}));
}

export function normalizeText(value: string): string {
	return (value || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

export function columnValues(row: EditorRow, column: RiskColumn): string[] {
	if (column.tab === ASSESSMENT_TAB) {
		return [row.values[column.index] || ''];
	}
	const lines = activeLines(row, column.tab);
	return lines.length ? lines.map(line => line.values[column.index] || '') : [''];
}

export function hasFilter(filter?: string[] | string): boolean {
	return Array.isArray(filter) ? filter.length > 0 : !!filter && !!filter.trim();
}

export function filterRows(rows: EditorRow[], filters: Filters, columns: RiskColumn[], onlyChanged: boolean,
		pinned: Set<string> = new Set()): EditorRow[] {
	const active = columns.filter(column => hasFilter(filters[column.id]));
	return rows.filter(row => (!onlyChanged || isRowChanged(row)) && (pinned.has(row.key) || active.every(column => {
		const filter = filters[column.id];
		const values = columnValues(row, column);
		if (Array.isArray(filter)) {
			return values.some(value => filter.includes(value));
		}
		const term = normalizeText(filter.trim());
		return values.some(value => normalizeText(value).includes(term));
	})));
}

export function changedTabs(rows: EditorRow[]): Set<string> {
	const tabs = new Set<string>();
	rows.forEach(row => {
		if (row.added || row.removed || row.values.some((value, index) => value !== row.original[index])) {
			tabs.add(ASSESSMENT_TAB);
		}
		SUB_TABS.forEach(tab => {
			if ((row.tabs[tab] || []).some(isLineChanged)) {
				tabs.add(tab);
			}
		});
	});
	return tabs;
}

export function highlightRange(text: string, term: string): [number, number] | null {
	const query = normalizeText(term.trim());
	if (!query) {
		return null;
	}
	let normalized = '';
	const origins: number[] = [];
	Array.from(text).forEach((character, index) => {
		const plain = normalizeText(character);
		normalized += plain;
		for (let position = 0; position < plain.length; position++) {
			origins.push(index);
		}
	});
	const start = normalized.indexOf(query);
	if (start < 0) {
		return null;
	}
	return [origins[start], origins[start + query.length - 1] + 1];
}

export function distinctValues(rows: EditorRow[], column: RiskColumn): string[] {
	const values = new Set<string>();
	rows.forEach(row => columnValues(row, column).forEach(value => values.add(value)));
	return Array.from(values).sort((first, second) => first.localeCompare(second, 'pt-BR'));
}

export function facetOptions(rows: EditorRow[], filters: Filters, columns: RiskColumn[], optionColumns: RiskColumn[],
		pinned: Set<string> = new Set()): Record<string, string[]> {
	const visible = filterRows(rows, filters, columns, false, pinned);
	return Object.fromEntries(optionColumns.map(column => {
		const filter = filters[column.id];
		if (!hasFilter(filter)) {
			return [column.id, distinctValues(visible, column)];
		}
		const { [column.id]: _, ...others } = filters;
		const options = distinctValues(filterRows(rows, others, columns, false, pinned), column);
		const selected = Array.isArray(filter) ? filter.filter(value => !options.includes(value)) : [];
		return [column.id, [...options, ...selected].sort((first, second) => first.localeCompare(second, 'pt-BR'))];
	}));
}

export function emptySelection(): SelectionState {
	return { rows: new Set(), lines: new Set() };
}

function rowLineIds(row: EditorRow): string[] {
	return SUB_TABS.flatMap(tab => activeLines(row, tab).map(line => line.id));
}

export function rowCheckState(selection: SelectionState, row: EditorRow, tab: string): CheckState {
	if (tab === ASSESSMENT_TAB) {
		return selection.rows.has(row.key) ? 'checked' : 'unchecked';
	}
	const lines = activeLines(row, tab);
	if (!lines.length) {
		return selection.rows.has(row.key) ? 'checked' : 'unchecked';
	}
	const selected = lines.filter(line => selection.lines.has(line.id)).length;
	return selected === lines.length ? 'checked' : selected ? 'indeterminate' : 'unchecked';
}

export function setRowsSelected(selection: SelectionState, rows: EditorRow[], selected: boolean): SelectionState {
	const nextRows = new Set(selection.rows);
	const nextLines = new Set(selection.lines);
	rows.forEach(row => {
		if (selected) {
			nextRows.add(row.key);
			rowLineIds(row).forEach(id => nextLines.add(id));
		} else {
			nextRows.delete(row.key);
			rowLineIds(row).forEach(id => nextLines.delete(id));
		}
	});
	return { rows: nextRows, lines: nextLines };
}

export function setRowSelected(selection: SelectionState, row: EditorRow, selected: boolean): SelectionState {
	return setRowsSelected(selection, [row], selected);
}

export function toggleRowSelection(selection: SelectionState, row: EditorRow, tab: string): SelectionState {
	return setRowSelected(selection, row, rowCheckState(selection, row, tab) !== 'checked');
}

export function toggleLineSelection(selection: SelectionState, row: EditorRow, tab: string, lineId: string): SelectionState {
	const lines = new Set(selection.lines);
	if (lines.has(lineId)) {
		lines.delete(lineId);
	} else {
		lines.add(lineId);
	}
	const rows = new Set(selection.rows);
	if (!activeLines(row, tab).every(line => lines.has(line.id))) {
		rows.delete(row.key);
	}
	return { rows, lines };
}

export function withoutLine(selection: SelectionState, lineId: string): SelectionState {
	if (!selection.lines.has(lineId)) {
		return selection;
	}
	const lines = new Set(selection.lines);
	lines.delete(lineId);
	return { rows: selection.rows, lines };
}

export function pruneSelection(selection: SelectionState, visibleRows: EditorRow[]): SelectionState {
	const keys = new Set(visibleRows.map(row => row.key));
	const lineIds = new Set(visibleRows.flatMap(rowLineIds));
	const rows = Array.from(selection.rows).filter(key => keys.has(key));
	const lines = Array.from(selection.lines).filter(id => lineIds.has(id));
	if (rows.length === selection.rows.size && lines.length === selection.lines.size) {
		return selection;
	}
	return { rows: new Set(rows), lines: new Set(lines) };
}

export function allSelectedState(selection: SelectionState, rows: EditorRow[], tab: string): CheckState {
	const candidates = rows.filter(row => !row.removed);
	if (!candidates.length) {
		return 'unchecked';
	}
	const states = candidates.map(row => rowCheckState(selection, row, tab));
	if (states.every(state => state === 'checked')) {
		return 'checked';
	}
	return states.some(state => state !== 'unchecked') ? 'indeterminate' : 'unchecked';
}

export function lineSelectionKey(selection: SelectionState, row: EditorRow, tab: string): string {
	return activeLines(row, tab).filter(line => selection.lines.has(line.id)).map(line => line.id).join('|');
}

export function resolveBulkValue(value: string): string {
	return value === EMPTY_OPTION ? '' : value;
}

export function applyBulk(rows: EditorRow[], visibleKeys: Set<string>, selection: SelectionState, tab: string,
		columns: RiskColumn[], bulk: BulkValues): { rows: EditorRow[]; addedLineIds: string[] } {
	const active = columns.filter(column => column.tab === tab && !!bulk[column.id]);
	const addedLineIds: string[] = [];
	if (!active.length) {
		return { rows, addedLineIds };
	}
	const utilized = active.find(isUtilizedEpi);
	const recommended = active.find(isRecommendedEpi);
	const mirrored = utilized || recommended;
	const utilizedOption = mirrored ? parseEpiOption(resolveBulkValue(bulk[mirrored.id])) : null;
	const assign = (values: string[]) => {
		const next = [...values];
		active.forEach(column => {
			next[column.index] = column.link === 'EPI' ? parseEpiOption(resolveBulkValue(bulk[column.id])).epi : resolveBulkValue(bulk[column.id]);
		});
		if (!utilized && utilizedOption) {
			next[UTILIZED_EPI] = utilizedOption.epi;
		}
		return next;
	};
	const mirrorCa = (row: EditorRow, lineIds: string[]) => utilizedOption
		? lineIds.reduce((current, lineId) => withCaCode(current, lineId, utilizedOption.epi ? utilizedOption.code : ''), row)
		: row;
	const next = rows.map(row => {
		if (row.removed || !visibleKeys.has(row.key)) {
			return row;
		}
		if (tab === ASSESSMENT_TAB) {
			return selection.rows.has(row.key) ? { ...row, values: assign(row.values) } : row;
		}
		const lines = row.tabs[tab] || [];
		if (!activeLines(row, tab).length) {
			if (!selection.rows.has(row.key)) {
				return row;
			}
			const line = createLine(row.key, tab, assign(Array(TAB_SIZES[tab]).fill('')));
			addedLineIds.push(line.id);
			return mirrorCa({ ...row, tabs: { ...row.tabs, [tab]: [...lines, line] } }, [line.id]);
		}
		const touched: string[] = [];
		const nextLines = lines.map(line => {
			if (line.removed || !selection.lines.has(line.id)) {
				return line;
			}
			touched.push(line.id);
			return { ...line, values: assign(line.values) };
		});
		return touched.length ? mirrorCa({ ...row, tabs: { ...row.tabs, [tab]: nextLines } }, touched) : row;
	});
	return { rows: next, addedLineIds };
}

export function riskKey(name: string): string {
	return (name || '').trim().toUpperCase();
}

export function buildLinkIndex(links: RiskLinkDTO[], catalog: RiskEpiCatalogDTO[]): LinkIndex {
	return {
		byRisk: new Map(links.map(link => [riskKey(link.risk), link] as [string, RiskLinkDTO])),
		catalog: new Map(catalog.map(item => [item.code, item] as [number, RiskEpiCatalogDTO]))
	};
}

export function linkOptions(index: LinkIndex, risk: string, kind: LinkKind): string[] {
	const link = index.byRisk.get(riskKey(risk));
	if (!link) {
		return [];
	}
	if (kind === 'EPI') {
		return link.epis;
	}
	if (kind === 'EPC') {
		return link.epcs;
	}
	if (kind === 'MEASURE') {
		return link.measures;
	}
	if (kind === 'SOURCE') {
		return link.sources || [];
	}
	const codes = new Set<number>();
	Object.values(link.caCodesByEpi || {}).forEach(list => list.forEach(code => codes.add(code)));
	return Array.from(codes).sort((first, second) => first - second).map(String);
}

export function intersectOptions(lists: string[][]): string[] {
	if (!lists.length) {
		return [];
	}
	return lists.slice(1).reduce((current, list) => current.filter(value => list.includes(value)), lists[0]);
}

export function invalidLinkedBulk(bulk: BulkValues, columns: RiskColumn[], risks: string[], index: LinkIndex): RiskColumn[] {
	return columns.filter(column => {
		const value = bulk[column.id];
		const link = column.link;
		if (!link || !value || value === EMPTY_OPTION) {
			return false;
		}
		const allowed = intersectOptions(risks.map(risk => columnLinkOptions(index, risk, column)));
		return (column.multiple ? splitMultiValue(value) : [value]).some(item => !allowed.includes(item));
	});
}

export const MULTI_VALUE_SEPARATOR = ' | ';

export function splitMultiValue(value: string): string[] {
	return (value || '').split('|').map(item => item.trim()).filter(item => !!item);
}

export function joinMultiValue(values: string[]): string {
	return values.map(item => item.trim()).filter(item => !!item).join(MULTI_VALUE_SEPARATOR);
}

export function withCurrentValues(column: RiskColumn, options: string[], current: string): string[] {
	if (!column.multiple) {
		return options;
	}
	return [...splitMultiValue(current).filter(item => !options.includes(item)), ...options];
}

export function outsideLinkValues(index: LinkIndex, risk: string, column: RiskColumn, value: string): string[] {
	if (!column.link || !value) {
		return [];
	}
	const allowed = linkOptions(index, risk, column.link);
	return (column.multiple ? splitMultiValue(value) : [value]).filter(item => !allowed.includes(item));
}

export function caLabel(index: LinkIndex, code: string): string {
	const item = index.catalog.get(Number(code));
	return item ? `${item.code} - ${item.name} - CA ${item.ca}` : code;
}

function compareCa(first: string, second: string): number {
	return first.localeCompare(second, 'pt-BR', { numeric: true });
}

function epiCodes(index: LinkIndex, risks: string[], epi: string): number[] {
	const codes = new Set<number>();
	risks.forEach(risk => (index.byRisk.get(riskKey(risk))?.caCodesByEpi?.[epi] || [])
		.filter(code => index.catalog.has(code))
		.forEach(code => codes.add(code)));
	return Array.from(codes).sort((first, second) => first - second);
}

export function epiOptionLabel(index: LinkIndex, option: string): string {
	const { epi, code } = parseEpiOption(option);
	if (!code) {
		return epi;
	}
	const ca = index.catalog.get(Number(code))?.ca;
	return `${epi} (${ca || 'CA não encontrado'})`;
}

export function columnLinkOptions(index: LinkIndex, risk: string, column: RiskColumn, current?: string): string[] {
	if (!column.link) {
		return [];
	}
	if (column.multiple) {
		return withCurrentValues(column, linkOptions(index, risk, column.link), current || '');
	}
	if (column.link !== 'EPI') {
		return linkOptions(index, risk, column.link);
	}
	const options = linkOptions(index, risk, 'EPI').flatMap(epi => {
		const byCa = new Map<string, number>();
		epiCodes(index, [risk], epi).forEach(code => {
			const ca = index.catalog.get(code)?.ca || '';
			if (!byCa.has(ca)) {
				byCa.set(ca, code);
			}
		});
		return byCa.size
			? Array.from(byCa.entries()).sort(([first], [second]) => compareCa(first, second)).map(([, code]) => epiOption(epi, code))
			: [epi];
	});
	return current && !options.includes(current) ? [current, ...options] : options;
}

export function epiOptionCa(index: LinkIndex, option: string): { epi: string; ca: string; missing: boolean } {
	const { epi, code } = parseEpiOption(option);
	const ca = code ? index.catalog.get(Number(code))?.ca || '' : '';
	return { epi, ca, missing: !!code && !ca };
}

export function linkOptionLabel(index: LinkIndex, column: RiskColumn, option: string): string {
	if (column.link === 'CA') {
		return caLabel(index, option);
	}
	return column.link === 'EPI' ? parseEpiOption(option).epi : option;
}

export function linkOptionSearchText(index: LinkIndex, column: RiskColumn, option: string): string {
	return column.link === 'EPI' ? epiOptionLabel(index, option) : linkOptionLabel(index, column, option);
}

export function domainOptions(options: Record<string, string[]>, column: RiskColumn, row?: EditorRow): string[] {
	if (column.fixedOptions) {
		return column.fixedOptions;
	}
	const key = column.domainKey || String(column.index);
	if (column.kind === 'suggest' && row) {
		const matrix = (row.values[MATRIX] || '').trim();
		return options[`${key}|${matrix}`] || options[key] || [];
	}
	return options[key] || [];
}

export function bulkRisks(rows: EditorRow[], selection: SelectionState, tab: string): string[] {
	const risks = new Set<string>();
	rows.forEach(row => {
		if (row.removed) {
			return;
		}
		const lineSelected = activeLines(row, tab).some(line => selection.lines.has(line.id));
		if (selection.rows.has(row.key) || lineSelected) {
			risks.add(row.values[RISK]);
		}
	});
	return Array.from(risks);
}

export function toFinishRows(rows: EditorRow[]): RiskSheetRowDTO[] {
	return rows.filter(row => !row.removed).map(row => ({
		key: row.key,
		sourceLine: row.sourceLine,
		values: row.values,
		tabs: Object.fromEntries(SUB_TABS.map(tab => [tab, distinctCaLines(tab, activeLines(row, tab)
			.map(line => line.values)
			.filter(values => values.some(value => (value || '').trim() !== '')))]))
	}));
}

function distinctCaLines(tab: string, lines: string[][]): string[][] {
	if (tab !== CA_TAB) {
		return lines;
	}
	const seen = new Set<string>();
	return lines.filter(values => {
		const code = (values[0] || '').trim();
		if (seen.has(code)) {
			return false;
		}
		seen.add(code);
		return true;
	});
}

export function companyOptionLabel(company: RiskCompanyDTO): string {
	return `${company.companyName} - ${company.companyId}`;
}

export function filterCompanies(options: RiskCompanyDTO[], input: string): RiskCompanyDTO[] {
	const term = normalizeText(input.trim());
	const digits = input.replace(/\D/g, '');
	if (!term) {
		return options;
	}
	const rank = (option: RiskCompanyDTO) => {
		const code = String(option.companyId);
		const name = normalizeText(option.companyName || '');
		if (code === term) {
			return 0;
		}
		if (code.startsWith(term)) {
			return 1;
		}
		if (name.startsWith(term)) {
			return 2;
		}
		if (code.includes(term)) {
			return 3;
		}
		if (name.includes(term)) {
			return 4;
		}
		return !!digits && (option.cnpj || '').replace(/\D/g, '').includes(digits) ? 5 : -1;
	};
	return options
		.map(option => ({ option, score: rank(option) }))
		.filter(item => item.score >= 0)
		.sort((first, second) => first.score - second.score)
		.map(item => item.option);
}

export function linkItems(link: RiskLinkDTO, type: string): string[] {
	if (type === 'EPI') {
		return link.epis;
	}
	if (type === 'EPC') {
		return link.epcs;
	}
	return link.measures;
}

export interface LinkSearchEntry {
	link: RiskLinkDTO;
	risk: string;
	texts: Record<string, string[]>;
}

export function buildLinkSearch(links: RiskLinkDTO[], catalog: Map<number, RiskEpiCatalogDTO>): LinkSearchEntry[] {
	return links.map(link => ({
		link,
		risk: normalizeText(link.risk),
		texts: Object.fromEntries(LINK_TYPES.map(type => [type, linkItems(link, type).flatMap(item => [
			normalizeText(item),
			...(type === 'EPI' ? (link.caCodesByEpi[item] || []).map(code => normalizeText(catalog.get(code)?.ca || '')) : [])
		])]))
	}));
}

export function filterLinks(entries: LinkSearchEntry[], riskSearch: string, itemSearch: string, types: string[]): RiskLinkDTO[] {
	const riskTerm = normalizeText(riskSearch.trim());
	const itemTerm = normalizeText(itemSearch.trim());
	return entries.filter(entry => (!riskTerm || entry.risk.includes(riskTerm))
		&& types.some(type => linkItems(entry.link, type).length)
		&& (!itemTerm || types.some(type => entry.texts[type].some(text => text.includes(itemTerm)))))
		.map(entry => entry.link);
}

export function estimateRowHeight(row: EditorRow, tab: string, readOnly: boolean): number {
	const lines = tab === ASSESSMENT_TAB ? 0 : (row.tabs[tab] || []).length;
	if (!lines) {
		return LINE_HEIGHT;
	}
	return lines * LINE_HEIGHT + (readOnly || row.removed ? 0 : NEW_LINE_HEIGHT);
}

export function countLabel(count: number, singular: string, plural: string): string {
	return `${count} ${count === 1 ? singular : plural}`;
}
